import { Box, Divider, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useRecoilState } from 'recoil';
import ResetButton from '~/components/Buttons/ResetButton';
import { Slider } from '~/components/Slider';
import { DEFAULT_DOSE_OPACITY_VALUE } from '~/engine/data/dose/DoseDistribution';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import {
    DOSE_OPACITY_STEP,
    doseOpacityState,
    doseOpacityWithinRange,
} from '~/state/atoms/dose/doseAtoms';

type DoseOpacityModifierProps = {
    showReset?: boolean;
};

export default function DoseOpacityModifier({
    showReset = true,
}: DoseOpacityModifierProps) {
    const currentDose = useCurrentDose();
    // The atom is the display truth; the display bridge carries it to
    // every drawn dose.
    const [doseOpacity, setDoseOpacity] =
        useRecoilState<number>(doseOpacityState);

    const handleSliderChange = (newValue: number): void => {
        setDoseOpacity(doseOpacityWithinRange(newValue / 100));
    };

    const handleReset = () => {
        setDoseOpacity(DEFAULT_DOSE_OPACITY_VALUE);
    };

    return (
        <Stack
            display="flex"
            flexDirection="column"
            gap={1}
            data-testid="dose-opacity-modifier"
        >
            <Slider
                label={
                    <Box display={'flex'} alignItems={'center'}>
                        <Typography fontSize={13}>
                            <FormattedMessage id="doseOpacity" />
                        </Typography>
                        {showReset && (
                            <>
                                <Divider
                                    flexItem
                                    orientation="vertical"
                                    sx={{ mx: 1, my: 0.5 }}
                                />
                                <ResetButton
                                    onClick={handleReset}
                                    disabled={!currentDose}
                                />
                            </>
                        )}
                    </Box>
                }
                value={doseOpacity * 100}
                onChange={(v) => handleSliderChange(v)}
                min={0}
                max={100}
                step={DOSE_OPACITY_STEP * 100}
                disabled={!currentDose}
                testId="dose-opacity-slider"
            />
        </Stack>
    );
}
